import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import {
  Home,
  User,
  Settings,
  HelpCircle,
  LogOut,
  ChevronDown,
  Shield,
  Crown,
  MessageSquare,
  Download,
  Globe,
  MoreHorizontal,
  X,
  BarChart2,
  Users,
  Calendar
} from 'lucide-react';
import MobileBottomNav from './MobileBottomNav';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [profileUrl, setProfileUrl] = useState<string>('');
  const { user, logout, subscription } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isInstallable, setIsInstallable] = useState(false);

  // Check if user has a paid plan (basic or premium)
  const isPaidPlan = subscription?.plan === 'basic' || subscription?.plan === 'premium';

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Fetch profile URL when user is available
  useEffect(() => {
    const fetchProfileUrl = async () => {
      if (user) {
        try {
          const response = await api.get('/profiles/me');
          if (response.data.success && response.data.profile?.profile_url) {
            setProfileUrl(response.data.profile.profile_url);
          }
        } catch (error) {
          console.error('Failed to fetch profile URL:', error);
        }
      }
    };

    fetchProfileUrl();
  }, [user]);



  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // PWA Install prompt capture
  useEffect(() => {
    // Check if prompt was already captured before React loaded
    if ((window as any).deferredInstallPrompt) {
      setDeferredPrompt((window as any).deferredInstallPrompt);
      setIsInstallable(true);
    }

    const handlePwaInstallReady = () => {
      if ((window as any).deferredInstallPrompt) {
        setDeferredPrompt((window as any).deferredInstallPrompt);
        setIsInstallable(true);
      }
    };

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
      (window as any).deferredInstallPrompt = e;
    };

    const handleAppInstalled = () => {
      setDeferredPrompt(null);
      setIsInstallable(false);
      (window as any).deferredInstallPrompt = null;
    };

    window.addEventListener('pwainstallready', handlePwaInstallReady);
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('pwainstallready', handlePwaInstallReady);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setDeferredPrompt(null);
      setIsInstallable(false);
    }
  };

  const isSuperAdmin = user?.role === 'super_admin';
  const isAdmin = isSuperAdmin || user?.role === 'admin';

  const baseNavigation = [
    { name: 'Home', href: '/dashboard', icon: Home },
    { name: 'Profile', href: '/dashboard/profile', icon: User },
    ...(profileUrl ? [{ name: 'View Profile', href: `/${profileUrl}`, icon: Globe }] : []),
    { name: 'Planner', href: '/dashboard/planner', icon: Calendar },
    { name: 'Analytics', href: '/dashboard/analytics', icon: BarChart2 },
    { name: 'Account', href: '/dashboard/account', icon: Settings },
    { name: 'Support', href: '/dashboard/support', icon: HelpCircle },
    { name: 'Leads', href: '/dashboard/leads', icon: MessageSquare },
    { name: 'Team Access', href: '/dashboard/useradmin', icon: Users },
  ];

  const navigation = [...baseNavigation];

  if (isAdmin && !isSuperAdmin) {
    navigation.push({ name: 'Admin Panel', href: '/dashboard/admin', icon: Shield });
  }

  if (isSuperAdmin) {
    navigation.push({ name: 'Super Admin Panel', href: '/dashboard/superadmin', icon: Crown });
  }

  const isActive = (href: string) => {
    return location.pathname === href;
  };

  // Bottom nav: Home, Profile, Leads, and a "More" action button
  const bottomNavigation = [
    { name: 'Home', href: '/dashboard', icon: Home },
    { name: 'Profile', href: '/dashboard/profile', icon: User },
    ...(profileUrl ? [{ name: 'Public', href: `/${profileUrl}`, icon: Globe }] : []),
    { name: 'Leads', href: '/dashboard/leads', icon: MessageSquare },
    { name: 'More', href: '#more', icon: MoreHorizontal, onAction: () => setDrawerOpen(true) },
  ];

  // Drawer items: Analytics, Support, Account, Team Access, Planner, and conditionally Admin/Super Admin
  const drawerNavigation = [
    { name: 'Planner', href: '/dashboard/planner', icon: Calendar },
    { name: 'Analytics', href: '/dashboard/analytics', icon: BarChart2 },
    { name: 'Support', href: '/dashboard/support', icon: HelpCircle },
    { name: 'Account', href: '/dashboard/account', icon: Settings },
    { name: 'Team Access', href: '/dashboard/useradmin', icon: Users },
    ...(isAdmin && !isSuperAdmin
      ? [{ name: 'Admin Panel', href: '/dashboard/admin', icon: Shield }]
      : []),
    ...(isSuperAdmin
      ? [{ name: 'Super Admin Panel', href: '/dashboard/superadmin', icon: Crown }]
      : []),
  ];

  return (
    <div className="min-h-screen bg-secondary-50">

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0">
        <div className="flex flex-col flex-grow bg-white border-r border-secondary-200">
          <div className="flex items-center h-16 px-4 border-b border-secondary-200">
            <Link to="/dashboard" className="flex items-center gap-3">
              <img src="/logo.png" alt="MY1QR" className="h-10 w-auto" />
              <span className="text-xl font-bold text-primary-600">MyOneQR</span>
            </Link>
          </div>
          <nav className="mt-8 flex-1 px-4 space-y-2">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${isActive(item.href)
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-secondary-600 hover:bg-secondary-100 hover:text-secondary-900'
                    }`}
                >
                  <Icon className="h-5 w-5 mr-3" />
                  {item.name}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64 pb-20 lg:pb-0">
        {/* Top bar */}
        <div className="sticky top-0 z-40 bg-white border-b border-secondary-200">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            {/* Left side - App name (mobile) */}
            <div className="flex items-center space-x-4">
              <Link to="/dashboard" className="block md:hidden flex items-center gap-3">
                <img src="/logo.png" alt="MY1QR" className="h-8 w-auto" />
                <span className="text-lg font-bold text-primary-600">MyOneQR</span>
              </Link>
            </div>

            {/* Right side - User Info & Dropdown */}
            <div className="flex items-center space-x-2">
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center space-x-3 p-2 rounded-lg hover:bg-secondary-50 transition-colors"
                >
                  {/* User Avatar */}
                  <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center text-white font-medium text-sm">
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </div>

                  {/* User Info - Hidden on mobile */}
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium text-secondary-900">{user?.name}</p>
                    <p className="text-xs text-secondary-500">{user?.email}</p>
                  </div>

                  <ChevronDown className={`h-4 w-4 text-secondary-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Dropdown Menu */}
                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-secondary-200 py-1 z-50 transform translate-x-0">
                    <div className="px-4 py-2 border-b border-secondary-100 md:hidden">
                      <p className="text-sm font-medium text-secondary-900 truncate">{user?.name}</p>
                      <p className="text-xs text-secondary-500 truncate" title={user?.email}>{user?.email}</p>
                    </div>

                    {isInstallable && isPaidPlan && (
                      <button
                        onClick={() => {
                          setDropdownOpen(false);
                          handleInstallClick();
                        }}
                        className="flex items-center w-full px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-50"
                      >
                        <Download className="h-4 w-4 mr-3" />
                        Install App
                      </button>
                    )}

                    <div className="border-t border-secondary-100 my-1"></div>

                    <button
                      onClick={() => {
                        setDropdownOpen(false);
                        handleLogout();
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      <LogOut className="h-4 w-4 mr-3" />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1">
          {children}
        </main>
      </div>

      {/* Mobile slide-in drawer (More menu) */}
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-50 bg-black/40 transition-opacity duration-300 lg:hidden ${drawerOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}
        onClick={() => setDrawerOpen(false)}
      />
      {/* Drawer panel */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-72 bg-white shadow-xl transition-transform duration-300 ease-in-out lg:hidden ${drawerOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-secondary-200">
          <span className="text-lg font-semibold text-primary-600">Menu</span>
          <button
            onClick={() => setDrawerOpen(false)}
            className="p-2 rounded-lg hover:bg-secondary-100 transition-colors"
          >
            <X className="h-5 w-5 text-secondary-600" />
          </button>
        </div>
        {/* Drawer items */}
        <nav className="mt-4 px-3 space-y-1">
          {drawerNavigation.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.href);
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setDrawerOpen(false)}
                className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${active
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-secondary-600 hover:bg-secondary-100 hover:text-secondary-900'
                  }`}
              >
                <Icon className="h-5 w-5 mr-3" />
                {item.name}
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav navigation={bottomNavigation} isActive={isActive} />
    </div>
  );
};

export default DashboardLayout;
